// R10: the user's ball appearance is identity, not mood. Keep this save separate
// from journey progress so Restart can clear the walk without erasing the design.
export const BALL_DESIGN_REVISION='diy-ball-r10';
export const BALL_DESIGN_SAVE_KEY='allwalks-ball-design-v1';
export const BALL_DESIGN_STORAGE_FORMAT='ball-design-v2';
export const BALL_DESIGN_SIZE={width:768,height:384};
export const BALL_BASE_COLORS=['#fffaf0','#e8c8aa','#d4c88f','#9db8a4','#93aebe','#b7a2bd','#d39a93','#665c57'];
export const BALL_INK_COLORS=['#26352f','#7f3f55','#386f68','#315f86','#a16a31','#f6e7c8','#6d5b8b','#bd6e5d'];
export const BALL_BRUSH_WIDTHS=[.012,.026,.052];
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
const color=v=>typeof v==='string'&&/^#[0-9a-f]{6}$/i.test(v)?v.toLowerCase():'#fffaf0';
const number=(v,f=0)=>Number.isFinite(Number(v))?Number(v):f;
const width=v=>clamp(number(v,.026)||.026,.006,.10);
const point=p=>({u:((number(p?.u)%1+1)%1),v:clamp(number(p?.v),0,1)});
export function createBallDesign(saved={}){
 saved=saved&&typeof saved==='object'?saved:{};
 const strokes=Array.isArray(saved.strokes)?saved.strokes.slice(-180).filter(s=>s&&typeof s==='object').map(s=>({color:color(s.color),width:width(s.width),points:Array.isArray(s.points)?s.points.slice(-700).map(point):[]})).filter(s=>s.points.length):[];
 return {revision:BALL_DESIGN_REVISION,baseColor:color(saved.baseColor),inkColor:color(saved.inkColor||BALL_INK_COLORS[0]),brushWidth:width(saved.brushWidth),strokes,started:!!saved.started};
}
export function serializeBallDesign(design){return createBallDesign(design);}
// Clone through the same normalization and size limits as loading a design.
export function cloneBallDesign(design){return createBallDesign(design);}
function encodePoints(points){
 const bytes=new Uint8Array(points.length*4),view=new DataView(bytes.buffer);
 for(let i=0;i<points.length;i++){const p=point(points[i]);view.setUint16(i*4,Math.round(p.u*65535),true);view.setUint16(i*4+2,Math.round(p.v*65535),true);}
 let raw='';for(let i=0;i<bytes.length;i+=8192)raw+=String.fromCharCode(...bytes.subarray(i,i+8192));return btoa(raw);
}
function decodePoints(encoded){
 if(typeof encoded!=='string')return [];
 const raw=atob(encoded);if(raw.length%4)throw new Error('Invalid compact ball points');
 const bytes=Uint8Array.from(raw,c=>c.charCodeAt(0)),view=new DataView(bytes.buffer),points=[];
 for(let i=0;i<raw.length;i+=4)points.push({u:view.getUint16(i,true)/65535,v:view.getUint16(i+2,true)/65535});
 return points;
}
export function serializeBallDesignStorage(design){
 const safe=createBallDesign(design);
 return {format:BALL_DESIGN_STORAGE_FORMAT,revision:safe.revision,baseColor:safe.baseColor,inkColor:safe.inkColor,brushWidth:safe.brushWidth,started:safe.started,strokes:safe.strokes.map(s=>({color:s.color,width:s.width,points:encodePoints(s.points)}))};
}
export function ballDesignStorageText(design){return JSON.stringify(serializeBallDesignStorage(design));}
function parseStoredBallDesign(saved){
 if(saved?.format!==BALL_DESIGN_STORAGE_FORMAT)return saved;
 const strokes=Array.isArray(saved.strokes)?saved.strokes.map(s=>{try{return {color:s?.color,width:s?.width,points:decodePoints(s?.points)};}catch{return null;}}).filter(Boolean):[];
 return {...saved,strokes};
}
export function loadBallDesign(storage){try{return createBallDesign(parseStoredBallDesign(JSON.parse((storage??localStorage).getItem(BALL_DESIGN_SAVE_KEY)||'{}')));}catch{return createBallDesign();}}
export function saveBallDesign(design,storage){try{(storage??localStorage).setItem(BALL_DESIGN_SAVE_KEY,ballDesignStorageText(design));return true;}catch{return false;}}
function unwrapStroke(points){
 const result=[{u:points[0].u,v:points[0].v}];
 for(let i=1;i<points.length;i++){const previous=result[i-1],raw=points[i];let u=raw.u;while(u-previous.u>.5)u-=1;while(u-previous.u<-.5)u+=1;result.push({u,v:raw.v});}
 return result;
}
function drawWrappedStroke(ctx,points,stroke,w,W,H){
 const unwrapped=unwrapStroke(points),us=unwrapped.map(p=>p.u),min=Math.min(...us),max=Math.max(...us);
 ctx.strokeStyle=stroke;ctx.lineWidth=w*H;ctx.lineCap='round';ctx.lineJoin='round';
 const firstShift=Math.ceil(-max),lastShift=Math.floor(1-min);
 for(let shift=firstShift;shift<=lastShift;shift++){ctx.beginPath();ctx.moveTo((unwrapped[0].u+shift)*W,unwrapped[0].v*H);for(let i=1;i<unwrapped.length;i++)ctx.lineTo((unwrapped[i].u+shift)*W,unwrapped[i].v*H);ctx.stroke();}
}
export function renderBallDesign(design,canvas){
 const {width:W,height:H}=BALL_DESIGN_SIZE;if(canvas.width!==W||canvas.height!==H){canvas.width=W;canvas.height=H;}
 const ctx=canvas.getContext('2d');ctx.clearRect(0,0,W,H);
 for(const s of design.strokes||[]){const pts=s.points||[];if(!pts.length)continue;if(pts.length===1){ctx.fillStyle=s.color;for(const shift of [-1,0,1]){ctx.beginPath();ctx.arc((pts[0].u+shift)*W,pts[0].v*H,s.width*H*.5,0,Math.PI*2);ctx.fill();}continue;}drawWrappedStroke(ctx,pts,s.color,s.width,W,H);}
 return canvas;
}
export function addStrokePoint(stroke,u,v){
 const p=point({u,v}),last=stroke.points.at(-1);if(last){let du=Math.abs(p.u-last.u);du=Math.min(du,1-du);if(Math.hypot(du,p.v-last.v)<.002)return false;}
 stroke.points.push(p);if(stroke.points.length>700)stroke.points.splice(1,1);return true;
}
